import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  Button,
  Textarea,
  ScrollArea,
  Paper,
  Text,
  Loader,
  Badge,
  Table,
  Stack,
  Group,
  Divider,
  Alert,
  ActionIcon,
  useMantineTheme,
} from '@mantine/core';
import { Send, AlertCircle } from 'tabler-icons-react';
import useAIChat from '../../hooks/useAIChat';
import { saveGeneratedHandlebarsTemplates } from '../../components/shared/hooks/template-functions';

// Define localized types for the message and fields matching the chatbox implementation
export interface LocalChatField {
  id?: string;
  key?: string;
  label?: string;
  type?: string;
  required?: boolean;
  optionsSource?: any;
  checklistNoteInputType?: string;
}

export interface LocalChatMessage {
  role: 'user' | 'model';
  createdAt?: number;
  type?: string;
  text?: string;
  reasoning?: string;
  data?: {
    fields?: LocalChatField[];
    [key: string]: any;
  };
  changedFields?: string[];
  confirmAction?: string;
  confirmPayload?: any;
  options?: string[];
  domain?: string;
  skill?: string;
}

interface AIFormGeneratorModalProps {
  opened: boolean;
  onClose: () => void;
  companyId: string;
  onSuccess: (templateId: string) => void;
}

const PREFIX = "Create a flex form template for: ";

export function AIFormGeneratorModal({
  opened,
  onClose,
  companyId,
  onSuccess,
}: AIFormGeneratorModalProps) {
  const theme = useMantineTheme();
  const [prompt, setPrompt] = useState(PREFIX);
  const [messages, setMessages] = useState<LocalChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isGeneratingTemplates, setIsGeneratingTemplates] = useState(false);
  const [pendingSkill, setPendingSkill] = useState<string | null>(null);

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { sendMessage, confirmAction, loading: aiLoading, error: aiError } = useAIChat();

  // Scroll to bottom on new messages
  useEffect(() => {
    if (scrollAreaRef.current && typeof scrollAreaRef.current.scrollTo === 'function') {
      scrollAreaRef.current.scrollTo({
        top: scrollAreaRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages, aiLoading]);

  // Focus textarea on open
  useEffect(() => {
    if (opened) {
      setPrompt(PREFIX);
      setMessages([]);
      setConversationId(null);
      setPendingSkill(null);
      setTimeout(() => {
        textareaRef.current?.focus();
        // Position cursor at end of prompt
        if (textareaRef.current) {
          textareaRef.current.selectionStart = textareaRef.current.selectionEnd = PREFIX.length;
        }
      }, 100);
    }
  }, [opened]);

  const isInitialTurn = messages.length === 0;

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    if (!isInitialTurn) {
      setPrompt(val);
      return;
    }
    if (val.startsWith(PREFIX)) {
      setPrompt(val);
    } else if (PREFIX.startsWith(val)) {
      // Re-enforce the locked prefix and prevent duplicating it when backspacing
      setPrompt(PREFIX);
    } else {
      // Prepend prefix to pasted or newly typed text
      setPrompt(PREFIX + val.replace(PREFIX, ""));
    }
  };

  const handleSend = async (textToSend: string) => {
    const trimmed = textToSend.trim();
    if (!trimmed || (isInitialTurn && trimmed === PREFIX.trim()) || aiLoading || isGeneratingTemplates) return;

    // Add user message to display
    const userMessage: LocalChatMessage = {
      role: 'user',
      text: textToSend,
      createdAt: Date.now(),
    };
    setMessages((prev) => [...prev, userMessage]);
    
    // Clear prompt input for follow-up turns
    setPrompt('');

    try {
      const activeSkill = pendingSkill || 'createFormTemplate';
      const response = await sendMessage(textToSend, companyId, conversationId, {
        context: { pendingSkill: activeSkill },
      });

      if (response) {
        const modelMessage: LocalChatMessage = {
          role: 'model',
          text: response.text ?? response.response ?? response.message,
          type: response.type,
          domain: response.domain,
          skill: response.skill,
          reasoning: response.reasoning,
          data: response.data,
          changedFields: response.changedFields,
          confirmAction: response.confirmAction,
          confirmPayload: response.confirmPayload,
          options: response.options,
          createdAt: Date.now(),
        };
        setMessages((prev) => [...prev, modelMessage]);

        if (response.conversationId) setConversationId(response.conversationId);
        setPendingSkill(response.pendingSkill || null);
      }
    } catch (err) {
      console.error("AI Error:", err);
    }
  };

  const handleConfirm = async (msg: LocalChatMessage) => {
    try {
      setIsGeneratingTemplates(true);
      const result = await confirmAction(msg.confirmPayload);

      if (
        msg.domain === 'formTemplate' &&
        msg.confirmPayload?.confirmData?.action === 'create' &&
        result.assetId &&
        Array.isArray(msg.data?.fields)
      ) {
        try {
          await saveGeneratedHandlebarsTemplates({
            templateId: result.assetId,
            companyId,
            fields: msg.data.fields,
          });
        } catch (templateError) {
          console.error('Error generating template configs after form creation:', templateError);
        }
        
        // Success: pass generated template ID up and close modal
        onSuccess(result.assetId);
        onClose();
      } else {
        // If not a standard template creation, print feedback in chat
        setPendingSkill(null);
        const successText = result.text || 'Action confirmed successfully.';
        setMessages((prev) => [...prev, { role: 'model', text: successText, createdAt: Date.now() }]);
      }
    } catch (err) {
      console.error('Error confirming action:', err);
    } finally {
      setIsGeneratingTemplates(false);
    }
  };

  const handleReject = (msg: LocalChatMessage) => {
    setPendingSkill(null);
    setMessages((prev) => [
      ...prev,
      {
        role: 'model',
        text: 'Suggestion dismissed. You can refine your request or try a different prompt.',
        createdAt: Date.now(),
      },
    ]);
  };

  const renderFieldsTable = (fields: LocalChatField[]) => {
    if (!Array.isArray(fields) || fields.length === 0) return null;
    return (
      <Table striped style={{ fontSize: '11px', marginTop: '8px', marginBottom: '8px' }}>
        <thead>
          <tr>
            <th style={{ width: '30px' }}>#</th>
            <th>Label</th>
            <th>Type</th>
            <th style={{ width: '50px' }}>Req</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field, idx) => {
            const isRequired = Boolean(field.required ?? (field as any).validation?.required);
            return (
              <tr key={field.key || idx}>
                <td>{idx + 1}</td>
                <td>{field.label}</td>
                <td>
                  <Badge size="xs" color="gray" variant="light">
                    {field.type || 'text'}
                  </Badge>
                </td>
                <td>{isRequired ? 'Yes' : 'No'}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  const isThinking = aiLoading || isGeneratingTemplates;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Generate Form with AI"
      size="lg"
      styles={{
        modal: {
          display: 'flex',
          flexDirection: 'column',
          height: '80vh',
        },
        body: {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          paddingBottom: theme.spacing.md,
        },
      }}
    >
      <ScrollArea style={{ flex: 1, paddingRight: '4px' }} viewportRef={scrollAreaRef} mb="md">
        {messages.length === 0 && !isThinking && (
          <Paper
            p="md"
            style={{
              background: theme.colors.gray[0],
              border: `1px dashed ${theme.colors.gray[3]}`,
              borderRadius: '10px',
              textAlign: 'center',
              marginTop: '20px',
            }}
          >
            <Text size="sm" weight={600}>
              Describe your new custom form
            </Text>
            <Text size="xs" color="dimmed" mt={4}>
              Type the details of the form you want to generate. For example:
              "farm equipment pre-start checklist including engine temperature, oil level check, and odometer reading."
            </Text>
          </Paper>
        )}

        <Stack spacing="md">
          {messages.map((msg, index) => {
            const isUser = msg.role === 'user';
            
            if (msg.type === 'suggestion') {
              return (
                <div key={index} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Paper
                    p="sm"
                    style={{
                      maxWidth: '90%',
                      background: theme.colors.gray[0],
                      border: `1px solid ${theme.colors['cap-purple']?.[2] || '#d6bcfa'}`,
                      borderRadius: '12px',
                    }}
                  >
                    <Group position="apart" mb="xs">
                      <Text size="xs" color="dimmed" weight={700}>AI Proposed Fields</Text>
                      <Badge size="xs" color="cap-purple" variant="light">
                        {msg.skill ? msg.skill.replace(/([A-Z])/g, ' $1').trim() : 'Form Template'}
                      </Badge>
                    </Group>
                    <Text size="sm">{msg.reasoning}</Text>
                    {msg.data?.fields && renderFieldsTable(msg.data.fields)}
                    <Group spacing="xs" mt="xs">
                      <Button
                        size="sm"
                        color="cap-navy"
                        style={{ minHeight: 36 }}
                        onClick={() => handleConfirm(msg)}
                        disabled={isThinking}
                      >
                        Accept & Create Form
                      </Button>
                      <Button
                        size="sm"
                        variant="default"
                        color="gray"
                        style={{ minHeight: 36 }}
                        onClick={() => handleReject(msg)}
                        disabled={isThinking}
                      >
                        Reject
                      </Button>
                    </Group>
                  </Paper>
                </div>
              );
            }

            if (msg.type === 'clarification') {
              return (
                <div key={index} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Paper
                    p="sm"
                    style={{
                      maxWidth: '85%',
                      background: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRadius: '12px',
                    }}
                  >
                    <Text size="xs" color="dimmed" mb={4}>AI</Text>
                    <Text size="sm">{msg.text}</Text>
                    {msg.options && msg.options.length > 0 && (
                      <Group spacing="xs" mt="sm">
                        {msg.options.map((opt) => (
                          <Button
                            key={opt}
                            size="sm"
                            variant="outline"
                            color="cap-blue"
                            style={{ minHeight: 34 }}
                            onClick={() => handleSend(opt)}
                            disabled={isThinking}
                          >
                            {opt}
                          </Button>
                        ))}
                      </Group>
                    )}
                  </Paper>
                </div>
              );
            }

            return (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: isUser ? 'flex-end' : 'flex-start',
                }}
              >
                <Paper
                  p="sm"
                  style={{
                    maxWidth: '80%',
                    background: isUser ? '#3b5bdb' : '#f1f3f5',
                    color: isUser ? '#fff' : '#000',
                    borderRadius: '12px',
                  }}
                >
                  <Text size="xs" style={{ color: isUser ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.5)' }} mb={4}>
                    {isUser ? 'You' : 'AI'}
                  </Text>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {msg.text}
                  </Text>
                </Paper>
              </div>
            );
          })}

          {isThinking && (
            <Group spacing="xs" px="sm">
              <Loader size="xs" color="cap-blue" />
              <Text size="xs" color="dimmed">AI is thinking...</Text>
            </Group>
          )}

          {aiError && (
            <Alert icon={<AlertCircle size={16} />} title="Error" color="red">
              {aiError}
            </Alert>
          )}
        </Stack>
      </ScrollArea>

      <Divider my="sm" />

      <Group spacing="xs" align="flex-end" noWrap>
        <Textarea
          ref={textareaRef}
          placeholder={isInitialTurn ? "Describe your form template..." : "Reply or ask for adjustments..."}
          value={prompt}
          onChange={handlePromptChange}
          disabled={isThinking}
          style={{ flex: 1 }}
          minRows={2}
          maxRows={4}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend(prompt);
            }
          }}
        />
        <ActionIcon
          color="cap-blue"
          variant="filled"
          size="xl"
          onClick={() => handleSend(prompt)}
          disabled={isThinking || !prompt.trim() || (isInitialTurn && prompt.trim() === PREFIX.trim())}
        >
          <Send size={18} />
        </ActionIcon>
      </Group>
    </Modal>
  );
}
